import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usersApi, cemeteryApi, graveSitesApi } from '../services/api';
import { confirm } from 'devextreme/ui/dialog';
import { DataGrid, Column, Editing, Paging, FilterRow, HeaderFilter, SearchPanel, Toolbar, Item, Form, Popup, MasterDetail, Pager, FilterPanel, Selection } from 'devextreme-react/data-grid';
import { Item as FormItem } from 'devextreme-react/form';
import { Button } from 'devextreme-react/button';
import { Tabs } from 'devextreme-react/tabs';
import { FileUploader } from 'devextreme-react/file-uploader';
import notify from 'devextreme/ui/notify';
import GraveSitePopupEditor from '../components/GraveSitePopupEditor';
import './Administration.css';

type ActiveTab = 'deceased' | 'users' | 'cemetery';

const Administration: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<ActiveTab>('deceased');
  const usersDataGridRef = useRef<any>(null);
  const graveSitesDataGridRef = useRef<any>(null);
  
  // Cemetery photo state
  const [currentCemeteryPhoto, setCurrentCemeteryPhoto] = useState<string | null>(null);
  const [cemeteryDataId, setCemeteryDataId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [northDirection, setNorthDirection] = useState<{ x: number; y: number } | null>(null);
  const [referencePoints, setReferencePoints] = useState<Array<{ id: string; x: number; y: number; name: string }>>([]);
  const [photoFileName, setPhotoFileName] = useState<string>('');
  const [photoDimensions, setPhotoDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  
  // Categories state for autocomplete
  const [categories, setCategories] = useState<string[]>([]);
  
  // GraveSite popup editor state
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupData, setPopupData] = useState<any>(null);
  const [isNewGraveSite, setIsNewGraveSite] = useState(false);
  
  // Pager configuration
  const allowedPageSizes = [5, 10, 20, 50];
  const displayMode = 'adaptive';
  const showPageSizeSelector = true;
  const showInfo = true;
  const showNavButtons = true;
  
  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Load existing cemetery data on component mount
  useEffect(() => {
    if (activeTab === 'cemetery') {
      loadCemeteryData();
    }
  }, [activeTab]);

  // Load categories for autocomplete
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await graveSitesApi.getCategories();
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    loadCategories();
  }, []);

  const loadCemeteryData = async () => {
    try {
      const data = await cemeteryApi.getCemeteryData();
      if (data) {
        setCemeteryDataId(data.id);
        
        // Construct the full image URL for production and development
        const imageUrl = data.photoPath.startsWith('http') 
          ? data.photoPath  // Absolute URL from API
          : `${window.location.origin}${data.photoPath}`; // Relative path, make it absolute
        
        console.log('Loading existing cemetery photo from:', imageUrl);
        setCurrentCemeteryPhoto(imageUrl);
        setPhotoFileName(data.photoFileName);
        setPhotoDimensions({ width: data.photoWidth, height: data.photoHeight });
        
        if (data.northDirectionX !== null && data.northDirectionY !== null) {
          setNorthDirection({ x: data.northDirectionX, y: data.northDirectionY });
        }
        
        const refPoints = data.referencePoints.map((rp: any) => ({
          id: rp.id.toString(),
          name: rp.name,
          x: rp.x,
          y: rp.y
        }));
        setReferencePoints(refPoints);
      }
    } catch (error) {
      console.error('Failed to load cemetery data:', error);
    }
  };

  const handleResetPassword = async (userId: string) => {
    try {
      const confirmed = await confirm(
        'Ali ste prepričani, da želite ponastaviti geslo tega uporabnika? Uporabnik bo moral nastaviti novo geslo ob naslednji prijavi.',
        'Ponastavitev gesla'
      );
      
      if (confirmed) {
        await usersApi.resetPassword(userId);
        // Force refresh of the DataGrid to show updated hasPassword status
        if (usersDataGridRef.current) {
          usersDataGridRef.current.instance().getDataSource().reload();
        }
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      alert('Napaka pri ponastavitvi gesla. Preverite konzolo za podrobnosti.');
    }
  };

  const handleFileUpload = async (e: any) => {
    try {
      setIsUploading(true);
      
      // DevExpress FileUploader provides files in the value array
      const files = e.value;
      if (!files || files.length === 0) {
        setIsUploading(false);
        return;
      }
      
      const file = files[0]; // Get the first (and only) file
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        notify('Prosim izberite veljavno slikovno datoteko.', 'error', 3000);
        setIsUploading(false);
        return;
      }
      
      // Upload to backend API
      const uploadResult = await cemeteryApi.uploadPhoto(file);
      console.log('Upload result:', uploadResult);
      
      // Construct the full image URL for production and development
      const imageUrl = uploadResult.photoPath.startsWith('http') 
        ? uploadResult.photoPath  // Absolute URL from API
        : `${window.location.origin}${uploadResult.photoPath}`; // Relative path, make it absolute
      
      console.log('Loading image from URL:', imageUrl);
      
      // Get image dimensions
      const img = new Image();
      img.onload = () => {
        console.log('Image loaded successfully:', img.width, 'x', img.height);
        setPhotoDimensions({ width: img.width, height: img.height });
        setCurrentCemeteryPhoto(imageUrl);
        setPhotoFileName(uploadResult.fileName);
        
        // Reset calibration when new photo is uploaded
        setNorthDirection(null);
        setReferencePoints([]);
        setCemeteryDataId(null);
        
        notify('Fotografija pokopališča je bila uspešno naložena!', 'success', 3000);
        setIsUploading(false);
      };
      
      img.onerror = (error) => {
        console.error('Image load error:', error);
        console.error('Failed to load image from URL:', imageUrl);
        console.error('Upload result photoPath:', uploadResult.photoPath);
        notify('Napaka pri branju slike. URL: ' + imageUrl, 'error', 5000);
        setIsUploading(false);
      };
      
      img.src = imageUrl;
      
    } catch (error) {
      console.error('File upload failed:', error);
      notify('Napaka pri nalaganju fotografije. Preverite konzolo za podrobnosti.', 'error', 3000);
      setIsUploading(false);
    }
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    
    if (!northDirection) {
      // First click sets north direction
      setNorthDirection({ x, y });
      notify('Sever je nastavljen. Sedaj kliknite na referenčne točke.', 'info', 3000);
    } else {
      // Subsequent clicks add reference points
      const pointName = prompt('Vnesite ime referenčne točke:');
      if (pointName) {
        const newPoint = {
          id: Date.now().toString(),
          x,
          y,
          name: pointName
        };
        setReferencePoints(prev => [...prev, newPoint]);
        notify(`Referenčna točka "${pointName}" je bila dodana.`, 'success', 2000);
      }
    }
  };

  const saveCemeteryCalibration = async () => {
    try {
      setIsSaving(true);
      
      if (!photoFileName) {
        notify('Najprej naložite fotografijo pokopališča.', 'error', 3000);
        return;
      }
      
      const calibrationData = {
        northDirectionX: northDirection?.x,
        northDirectionY: northDirection?.y,
        referencePoints: referencePoints.map(rp => ({
          name: rp.name,
          x: rp.x,
          y: rp.y
        }))
      };
      
      let result;
      if (cemeteryDataId) {
        // Update existing data
        result = await cemeteryApi.updateCemeteryData(cemeteryDataId, calibrationData);
      } else {
        // Create new data
        const newData = {
          photoFileName,
          photoWidth: photoDimensions.width,
          photoHeight: photoDimensions.height,
          ...calibrationData
        };
        result = await cemeteryApi.saveCemeteryData(newData);
        setCemeteryDataId(result.id);
      }
      
      notify('Kalibracija pokopališča je bila uspešno shranjena!', 'success', 3000);
    } catch (error) {
      console.error('Failed to save cemetery calibration:', error);
      notify('Napaka pri shranjevanju kalibracije. Poskusite znova.', 'error', 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const resetCemeteryCalibration = () => {
    setNorthDirection(null);
    setReferencePoints([]);
    notify('Kalibracija je bila ponastavljena.', 'info', 2000);
  };



  const navigationTabs = [
    { id: 'deceased', text: 'Upravljanje pokojnih oseb', icon: 'card' },
    { id: 'users', text: 'Upravljanje uporabnikov', icon: 'user' },
    { id: 'cemetery', text: 'Podatki pokopališča', icon: 'pinmap' }
  ];

  const renderDeceasedManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Upravljanje grobnih mest in pokojnih oseb</h2>
        <p className="sectionDescription">
          Pregled grobnih mest z možnostjo upravljanja pokojnih oseb v posameznem grobu
        </p>
      </div>



      <div className="gridContainer">
        <DataGrid
          ref={graveSitesDataGridRef}
          dataSource={graveSitesApi.createStore()}
          showBorders={true}
          showRowLines={true}
          showColumnLines={false}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
          remoteOperations={{ filtering: true, sorting: true, paging: true }}
          height="auto"
          wordWrapEnabled={true}
          allowColumnReordering={true}
          allowColumnResizing={true}
          //columnResizingMode="widget"
          onEditingStart={async (e) => {
            try {
              // Fetch full grave site data including location and photo fields
              const fullData = await graveSitesApi.getById(e.data.id);
              setPopupData(fullData);
              setIsNewGraveSite(false);
              setPopupVisible(true);
            } catch (error) {
              console.error('Failed to fetch grave site data:', error);
              notify('Napaka pri pridobivanju podatkov', 'error', 3000);
            }
            e.cancel = true;
          }}
          onInitNewRow={(_e) => {
            setPopupData({});
            setIsNewGraveSite(true);
            setPopupVisible(true);
          }}
          onRowInserting={(e) => {
            // Prevent default insert - we handle it in the popup
            e.cancel = true;
          }}
          onRowUpdating={(e) => {
            // Prevent default update - we handle it in the popup
            e.cancel = true;
          }}
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} placeholder="Iskanje po vseh stolpcih..." width={"35vw"} />
          
          <FilterPanel visible={true} />
          <Paging enabled={true} defaultPageSize={10} />
          <Pager
            visible={true}
            allowedPageSizes={allowedPageSizes}
            displayMode={displayMode}
            showPageSizeSelector={showPageSizeSelector}
            showInfo={showInfo}
            showNavigationButtons={showNavButtons} />
          
          <Editing 
            mode="popup" 
            allowUpdating={true} 
            allowAdding={true} 
            allowDeleting={true}
            confirmDelete={true}
            useIcons={true}
            texts={{
              confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati ta zapis?',
              deleteRow: 'Izbriši',
              editRow: 'Uredi',
              saveRowChanges: 'Shrani',
              cancelRowChanges: 'Prekliči',
              addRow: 'Dodaj novo'
            }}
            popup={{
              visible: false // Hide built-in popup, use custom popup instead
            }}
          />

          <Selection mode="multiple" showCheckBoxesMode="always" />

          <Toolbar>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
            <Item name="columnChooserButton" />
            <Item
              widget="dxButton"
              locateInMenu="auto"
              showText="inMenu"
              options={{
                icon: 'xlsxfile',
                //text: 'Izvozi vse',
                hint: 'Izvozi vsa grobna mesta v Excel',
                onClick: async () => {
                  try {
                    await graveSitesApi.exportAll();
                  } catch {
                    notify('Napaka pri izvozu', 'error', 3000);
                  }
                },
              }}
            />
            <Item
              widget="dxButton"
              locateInMenu="auto"
              showText="inMenu"
              options={{
                icon: 'export',
                //text: 'Izvozi izbrane',
                hint: 'Izvozi izbrana grobna mesta v Excel',
                onClick: async () => {
                  const grid = graveSitesDataGridRef.current?.instance();
                  if (!grid) return;
                  const selected: any[] = grid.getSelectedRowsData();
                  if (!selected.length) {
                    notify('Ni izbranih vrstic', 'warning', 2500);
                    return;
                  }
                  const ids: number[] = selected.map((r: any) => r.id);
                  try {
                    await graveSitesApi.exportSelected(ids);
                  } catch {
                    notify('Napaka pri izvozu', 'error', 3000);
                  }
                },
              }}
            />
          </Toolbar>

          {/* Master Grid Columns - Grave Sites */}
          <Column 
            dataField="graveCode" 
            caption="Oznaka groba" 
            minWidth={120}
            allowEditing={false}
          />
          <Column 
            dataField="section" 
            caption="Odsek" 
            minWidth={80}
            validationRules={[{ type: 'required', message: 'Odsek je obvezen' }]}
          />
          <Column 
            dataField="graveRow" 
            caption="Vrsta" 
            minWidth={80}
            dataType="number"
            validationRules={[{ type: 'required', message: 'Vrsta je obvezna' }]}
          />
          <Column 
            dataField="sequentialNumber" 
            caption="Zaporedna" 
            minWidth={100}
            validationRules={[{ type: 'required', message: 'Zaporedna številka je obvezna' }]}
          />
          <Column 
            dataField="category" 
            caption="Kategorija" 
            minWidth={100}
          />
          <Column 
            dataField="department" 
            caption="Oddelek" 
            minWidth={80}
          />
          <Column 
            dataField="deceasedCount" 
            caption="Št. pokojnih" 
            minWidth={100}
            allowEditing={false}
          />
          <Column 
            dataField="deceasedNames" 
            caption="Pokojni" 
            minWidth={200}
            allowEditing={false}
            allowFiltering={true}
            allowSearch={true}
          />
          <Column 
            dataField="isActive" 
            caption="Aktiven" 
            dataType="boolean" 
            minWidth={80}
          />
          
          {/* Command column for edit/delete buttons */}
          <Column 
            type="buttons" 
            fixed={true}
            fixedPosition="right"
            width={100}
            buttons={[
              {
                name: 'edit',
                hint: 'Uredi',
                icon: 'edit'
              },
              {
                name: 'delete',
                hint: 'Izbriši',
                icon: 'trash'
              }
            ]}
          />

          {/* Master-Detail Template for Deceased */}
          <MasterDetail
            enabled={true}
            component={(props: any) => (
              <div style={{ padding: '0px' }}>
                <h4>Pokojni v grobu: {props.data.data.graveCode}</h4>
                <DataGrid
                  dataSource={graveSitesApi.getDeceasedByGrave(props.data.data.id)}
                  showBorders={true}
                  columnAutoWidth={true}
                  //height={350}
                >
                  <Paging enabled={true} defaultPageSize={5} />
                  {/* <Editing 
                    mode="row" 
                    allowUpdating={true} 
                    allowAdding={true} 
                    allowDeleting={true}
                    texts={{
                      confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati ta zapis?',
                      deleteRow: 'Izbriši',
                      editRow: 'Uredi',
                      saveRowChanges: 'Shrani',
                      cancelRowChanges: 'Prekliči',
                      addRow: 'Dodaj pokojnega'
                    }}
                  /> */}
                  
                  <Column dataField="fullName" caption="Polno ime" minWidth={180} />
                  <Column dataField="displayBirthDate" caption="Rojstvo" minWidth={100} />
                  <Column dataField="displayDeathDate" caption="Smrt" minWidth={100} />
                  <Column dataField="age" caption="Starost" minWidth={80} allowEditing={false} />
                  <Column dataField="graveType" caption="Tip groba" minWidth={100} />
                  <Column dataField="allowPublication" caption="Objava dovoljena" dataType="boolean" minWidth={120} />
                </DataGrid>
              </div>
            )}
          />
        </DataGrid>
        
        {/* Custom Popup Editor */}
        <GraveSitePopupEditor
          visible={popupVisible}
          data={popupData}
          isNew={isNewGraveSite}
          categories={categories}
          onHiding={() => setPopupVisible(false)}
          onSaved={() => {
            if (graveSitesDataGridRef.current) {
              graveSitesDataGridRef.current.instance().refresh();
            }
          }}
        />
      </div>
    </>
  );

  const renderCemeteryManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Podatki pokopališča</h2>
        <p className="sectionDescription">
          Upravljajte letalsko fotografijo pokopališča in nastavite referenčne točke za natančno lociranje grobov
        </p>
      </div>

      <div className="cemeteryContainer">
        <div className="uploadSection">
          <h3>Nalaganje letalske fotografije</h3>
          <p className="uploadDescription">
            Naložite visokokakovostno letalsko fotografijo pokopališča. 
            Priporočena je ločljivost vsaj 2000x2000 pikslov za natančno označevanje grobov.
          </p>
          
          <FileUploader
            selectButtonText="Izberi fotografijo"
            labelText="ali povlecite datoteko sem"
            accept="image/*"
            uploadMode="useForm"
            onValueChanged={handleFileUpload}
            allowCanceling={true}
            allowedFileExtensions={['.jpg', '.jpeg', '.png', '.gif', '.bmp']}
            maxFileSize={10000000} // 10MB
            multiple={false}
            showFileList={true}
            disabled={isUploading}
          />
          
          {isUploading && (
            <div className="uploadingIndicator">
              <p>Nalaganje fotografije...</p>
            </div>
          )}
        </div>

        {currentCemeteryPhoto && (
          <div className="imagePreviewSection">
            <div className="imageControls">
              <h3>Kalibracija fotografije</h3>
              <p className="calibrationInstructions">
                <strong>Korak 1:</strong> Kliknite na fotografijo, da označite smer severa.<br />
                <strong>Korak 2:</strong> Dodajte referenčne točke (npr. vhod, spomenik) za natančno pozicioniranje.
              </p>
              
              <div className="controlButtons">
                <Button
                  text="Shrani kalibracijo"
                  type="default"
                  stylingMode="contained"
                  onClick={saveCemeteryCalibration}
                  disabled={isSaving || !photoFileName}
                  icon="save"
                />
                <Button
                  text="Ponastavi kalibracijo"
                  type="normal"
                  stylingMode="outlined"
                  onClick={resetCemeteryCalibration}
                  icon="refresh"
                />
              </div>
            </div>

            <div className="imageContainer">
              <div className="imageWrapper" style={{ position: 'relative' }}>
                <img 
                  src={currentCemeteryPhoto}
                  alt="Fotografija pokopališča"
                  className="cemeteryPhoto"
                  onClick={handleImageClick}
                  style={{ 
                    width: '100%', 
                    maxWidth: '800px', 
                    height: 'auto',
                    cursor: 'crosshair',
                    border: '2px solid #ddd',
                    borderRadius: '8px'
                  }}
                />
                
                {/* North direction marker */}
                {northDirection && (
                  <div 
                    className="north-marker"
                    style={{
                      position: 'absolute',
                      left: `${northDirection.x}%`,
                      top: `${northDirection.y}%`,
                      transform: 'translate(-50%, -50%)',
                      width: '30px',
                      height: '30px',
                      backgroundColor: 'red',
                      borderRadius: '50%',
                      border: '3px solid white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      zIndex: 10,
                      cursor: 'pointer'
                    }}
                    title="Sever"
                  >
                    <div style={{ 
                      position: 'absolute',
                      top: '-15px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      fontSize: '20px'
                    }}>
                      ⬆️
                    </div>
                  </div>
                )}
                
                {/* Reference point markers */}
                {referencePoints.map((point) => (
                  <div 
                    key={point.id}
                    className="reference-marker"
                    style={{
                      position: 'absolute',
                      left: `${point.x}%`,
                      top: `${point.y}%`,
                      transform: 'translate(-50%, -50%)',
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'blue',
                      borderRadius: '50%',
                      border: '2px solid white',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                      zIndex: 10,
                      cursor: 'pointer'
                    }}
                    title={point.name}
                  />
                ))}
              </div>
            </div>

            <div className="calibrationInfo">
              <h4>Stanje kalibracije</h4>
              <div className="calibrationStatus">
                <p><strong>Sever:</strong> {northDirection ? '✅ Nastavljen' : '❌ Ni nastavljen'}</p>
                <p><strong>Referenčne točke:</strong> {referencePoints.length} dodanih</p>
                <p><strong>Status:</strong> {cemeteryDataId ? '💾 Shranjeno' : '⚠️ Ni shranjeno'}</p>
              </div>
              
              {isSaving && (
                <div className="savingIndicator">
                  <p>Shranjevanje kalibracije...</p>
                </div>
              )}
              
              {referencePoints.length > 0 && (
                <div className="referencePointsList">
                  <h5>Referenčne točke:</h5>
                  <ul>
                    {referencePoints.map((point, index) => (
                      <li key={point.id}>
                        {index + 1}. {point.name} ({point.x.toFixed(1)}%, {point.y.toFixed(1)}%)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  const renderUserManagement = () => (
    <>
      <div className="sectionHeader">
        <h2 className="sectionTitle">Upravljanje uporabnikov</h2>
        <p className="sectionDescription">
          Upravljajte uporabniške račune, vloge in dovoljenja sistema
        </p>
      </div>

      <div className="gridContainer">
        <DataGrid
          ref={usersDataGridRef}
          dataSource={usersApi.createStore()}
          showBorders={true}
          showRowLines={true}
          showColumnLines={false}
          rowAlternationEnabled={true}
          columnAutoWidth={true}
          remoteOperations={{ filtering: true, sorting: true, paging: true }}
          height="auto"
          wordWrapEnabled={true}
          allowColumnReordering={true}
          allowColumnResizing={true}
          columnResizingMode="widget"
        >
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <SearchPanel visible={true} placeholder="Iskanje uporabnikov..." />          
          
          <FilterPanel visible={true} />
          <Paging enabled={true} defaultPageSize={10} />
          <Pager
            visible={true}
            allowedPageSizes={allowedPageSizes}
            displayMode={displayMode}
            showPageSizeSelector={showPageSizeSelector}
            showInfo={showInfo}
            showNavigationButtons={showNavButtons} />

          <Editing 
            mode="popup" 
            allowUpdating={true} 
            allowAdding={true} 
            allowDeleting={true}
            confirmDelete={true}
            texts={{
              confirmDeleteMessage: 'Ali ste prepričani, da želite izbrisati tega uporabnika?',
              deleteRow: 'Izbriši',
              editRow: 'Uredi',
              saveRowChanges: 'Shrani',
              cancelRowChanges: 'Prekliči',
              addRow: 'Dodaj uporabnika'
            }}
          >
            <Popup 
              title="Upravljanje uporabnika" 
              showTitle={true} 
              width={600} 
              height={500}
            />
            <Form colCount={2}>
              <FormItem dataField="email" colSpan={2} />
              <FormItem dataField="firstName" />
              <FormItem dataField="lastName" />
              <FormItem dataField="isActive" />
            </Form>
          </Editing>
          
          <Toolbar>
            <Item name="addRowButton" />
            <Item name="searchPanel" />
            <Item name="columnChooserButton" />
          </Toolbar>

          <Column 
            dataField="email" 
            caption="E-poštni naslov" 
            minWidth={180}
            validationRules={[
              { type: 'required', message: 'E-poštni naslov je obvezen' },
              { type: 'email', message: 'Napačen format e-poštnega naslova' }
            ]}
          />
          <Column 
            dataField="firstName" 
            caption="Ime" 
            minWidth={120}
            validationRules={[{ type: 'required', message: 'Ime je obvezno' }]}
          />
          <Column 
            dataField="lastName" 
            caption="Priimek" 
            minWidth={120}
            validationRules={[{ type: 'required', message: 'Priimek je obvezen' }]}
          />
          <Column 
            dataField="role" 
            caption="Vloga" 
            minWidth={140}
            allowEditing={false}
            cellRender={(_cellInfo) => (
              <span className="role-badge role-admin">Administrator</span>
            )}
          />
          <Column 
            dataField="isActive" 
            caption="Status" 
            dataType="boolean" 
            minWidth={100}
            cellRender={(cellInfo) => {
              const isActive = cellInfo.value;
              return (
                <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
                  {isActive ? '✓ Aktiven' : '✗ Neaktiven'}
                </span>
              );
            }}
          />
          <Column 
            dataField="createdDate" 
            caption="Ustvarjen" 
            dataType="date" 
            minWidth={100}
            format="dd.MM.yyyy"
            allowEditing={false}
          />
          <Column 
            dataField="lastLogin" 
            caption="Zadnja prijava" 
            dataType="datetime" 
            minWidth={140}
            format="dd.MM.yyyy HH:mm"
            allowEditing={false}
            calculateDisplayValue={(rowData) => {
              if (!rowData.lastLogin) return 'Nikoli';
              const now = new Date();
              const lastLogin = new Date(rowData.lastLogin);
              const diffMs = now.getTime() - lastLogin.getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              
              if (diffDays === 0) return 'Danes';
              if (diffDays === 1) return 'Včeraj';
              if (diffDays < 7) return `Pred ${diffDays} dni`;
              return lastLogin.toLocaleDateString('sl-SI');
            }}
          />
          <Column 
            caption="Geslo" 
            minWidth={120}
            allowEditing={false}
            allowSorting={false}
            allowFiltering={false}
            cellRender={(cellInfo) => {
              const hasPassword = cellInfo.data.hasPassword; // We'll need to add this to the API response
              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {hasPassword ? (
                    <button 
                      className="password-action-btn reset"
                      onClick={() => handleResetPassword(cellInfo.data.id)}
                      title="Ponastavi geslo"
                    >
                      🔑 Ponastavi
                    </button>
                  ) : (
                    <span className="no-password-badge">Ni nastavljeno</span>
                  )}
                </div>
              );
            }}
          />
        </DataGrid>
      </div>
    </>
  );

  return (
    <div className="viewport">
      <div className="container">
        {/* Header */}
        <div className="header">
          <div className="headerContent">
            <div className="brandSection">
              <h1 className="brandTitle">GraveSearch</h1>
              <p className="brandSubtitle">Admin nadzorna plošča</p>
            </div>
            
            {/* Navigation */}
            <div className="navigationSection">
              <Tabs
                dataSource={navigationTabs}
                selectedIndex={activeTab === 'deceased' ? 0 : activeTab === 'users' ? 1 : 2}
                onSelectionChanged={(e) => {
                  if (e.addedItems && e.addedItems.length > 0) {
                    setActiveTab(e.addedItems[0].id as ActiveTab);
                  }
                }}
              />
            </div>
            
            <div className="userSection">
              <div className="userInfo">
                <span className="userName">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="userEmail">{user?.email}</span>
              </div>
              <Button
                text="Odjava"
                type="normal"
                stylingMode="contained"
                onClick={handleLogout}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="contentSection">
          {activeTab === 'deceased' && renderDeceasedManagement()}
          {activeTab === 'users' && renderUserManagement()}
          {activeTab === 'cemetery' && renderCemeteryManagement()}
        </div>
      </div>
    </div>
  );
};

export default Administration;